import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import tokenStore from '../utils/tokenStore';
import { fetchWithAuth } from '../utils/authUtils';
import AiDisclaimerBanner from './Legal/AiDisclaimerBanner';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import { FEATURES } from '../constants/featureAccess';
import decodedJWT from '../middleWare/decodedJWT';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
const AI_WELCOME =
  'Chào bạn, tôi là trợ lý AI TamCare — hỗ trợ tra cứu thông tin sức khỏe phổ thông, chỉ mang tính tham khảo.\n\n' +
  'Tôi không thay thế bác sĩ và không chẩn đoán bệnh. TamCare chỉ lưu/hiển thị thông tin bạn nhập.\n\n' +
  '⚠️ Không dùng thay ý kiến bác sĩ. Nguy hiểm: gọi 115 hoặc đến cơ sở y tế.';
const SEND_MIN_INTERVAL_MS = parseInt(process.env.REACT_APP_CHAT_SEND_MIN_INTERVAL_MS || '1200', 10);

const getWelcomeMessages = () => [{ id: 'welcome', sender: 'ai', text: AI_WELCOME }];

const parseAttachmentUrls = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw
      .map((item) => {
        if (typeof item === 'string' && item) return item;
        if (item && typeof item.url === 'string' && item.url) return item.url;
        return null;
      })
      .filter(Boolean);
  }
  return [];
};

const mapServerMessage = (row) => ({
  id: row.id ?? Date.now() + Math.random(),
  sender: row.sender === 'user' ? 'user' : 'ai',
  text: row.text || '',
  attachmentUrls: parseAttachmentUrls(row.attachmentUrls),
});

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${path}`;
};

const ChatBox = () => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(() => {
    try {
      const raw = localStorage.getItem('tamcare_chat_open_v1');
      if (raw === '1') return true;
    } catch (e) {
      // ignore
    }
    return false;
  });

  const [messages, setMessages] = useState(getWelcomeMessages);
  const [input, setInput] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const lastSendAtRef = useRef(0);
  const messagesEndRef = useRef(null);

  const [isLoggedIn, setIsLoggedIn] = useState(() => Boolean(tokenStore.getToken()));
  const { canUse, loading: accessLoading } = useFeatureAccess();
  const userRole = decodedJWT()?.role;
  const isCareUser = userRole === 'elderly' || userRole === 'relative';
  const showAiChat = !isLoggedIn || userRole === 'admin' || !isCareUser || canUse(FEATURES.AI_CHAT);
  useEffect(() => {
    const unsub = tokenStore.subscribe(() => {
      setIsLoggedIn(Boolean(tokenStore.getToken()));
    });
    return unsub;
  }, []);

  const loadHistoryFromServer = useCallback(async () => {
    if (!tokenStore.getToken()) {
      setMessages(getWelcomeMessages());
      return;
    }
    if (accessLoading) return;
    if (isCareUser && !canUse(FEATURES.AI_CHAT)) {
      setMessages(getWelcomeMessages());
      return;
    }

    setHistoryLoading(true);
    try {
      const response = await fetchWithAuth(`${API_BASE}/api/ai/chat/history`);
      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        setMessages(getWelcomeMessages());
        return;
      }

      const list = Array.isArray(payload?.messages) ? payload.messages.map(mapServerMessage) : [];
      setMessages(list.length ? list : getWelcomeMessages());
    } catch (e) {
      setMessages(getWelcomeMessages());
    } finally {
      setHistoryLoading(false);
    }
  }, [accessLoading, isCareUser, canUse]);

  useEffect(() => {
    if (isLoggedIn) {
      loadHistoryFromServer();
    } else {
      setMessages(getWelcomeMessages());
      try {
        localStorage.removeItem('tamcare_chat_messages_v1');
      } catch (e) {
        // ignore
      }
    }
  }, [isLoggedIn, loadHistoryFromServer]);

  useEffect(() => {
    if (open && isLoggedIn) {
      loadHistoryFromServer();
    }
  }, [open, isLoggedIn, loadHistoryFromServer]);

  useEffect(() => {
    try {
      localStorage.setItem('tamcare_chat_open_v1', open ? '1' : '0');
    } catch (e) {
      // ignore
    }
  }, [open]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, historyLoading]);

  const speechSupported = useMemo(() => Boolean(window.SpeechRecognition || window.webkitSpeechRecognition), []);

  const styles = useMemo(
    () => ({
      bubble: {
        position: 'fixed',
        bottom: 25,
        right: 25,
        width: 68,
        height: 68,
        backgroundColor: '#28a745',
        color: 'white',
        borderRadius: '50%',
        display: open ? 'none' : 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 28,
        cursor: 'pointer',
        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
        zIndex: 1000,
      },
      popup: {
        position: 'fixed',
        bottom: open ? 25 : 110,
        right: 25,
        width: 'min(520px, calc(100vw - 40px))',
        height: 'min(600px, calc(100vh - 140px))',
        background: '#fff',
        borderRadius: 15,
        boxShadow: '0 5px 25px rgba(0,0,0,0.15)',
        display: open ? 'flex' : 'none',
        flexDirection: 'column',
        zIndex: 1000,
        overflow: 'hidden',
        transformOrigin: 'bottom right',
      },
      header: {
        background: '#28a745',
        color: 'white',
        padding: 16,
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      },
      closeBtn: {
        cursor: 'pointer',
        fontSize: 20,
      },
      messages: {
        flex: 1,
        padding: 16,
        overflowY: 'auto',
        background: '#f4f7f6',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      },
      msg: {
        padding: '10px 14px',
        borderRadius: 18,
        maxWidth: '90%',
        wordWrap: 'break-word',
        lineHeight: 1.5,
      },
      msgUser: {
        background: '#007bff',
        color: '#fff',
        alignSelf: 'flex-end',
        borderBottomRightRadius: 3,
      },
      msgAi: {
        background: '#e9e9eb',
        color: '#000',
        alignSelf: 'flex-start',
        borderBottomLeftRadius: 3,
      },
      msgLoading: {
        alignSelf: 'flex-start',
      },
      preview: {
        display: selectedFiles.length ? 'flex' : 'none',
        gap: 5,
        overflowX: 'auto',
        padding: 8,
        background: '#fff',
        borderTop: '1px solid #eee',
      },
      previewItem: { position: 'relative' },
      previewImg: {
        width: 40,
        height: 40,
        objectFit: 'cover',
        borderRadius: 5,
      },
      removeBtn: {
        position: 'absolute',
        top: -5,
        right: -5,
        background: 'red',
        color: 'white',
        width: 16,
        height: 16,
        borderRadius: '50%',
        fontSize: 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        border: '1px solid white',
      },
      inputWrap: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: 12,
        background: '#fff',
        borderTop: '1px solid #ddd',
      },
      sendBtn: {
        background: '#28a745',
        color: '#fff',
        borderRadius: '50%',
        width: 36,
        height: 36,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 16,
        cursor: 'pointer',
        border: 'none',
      },
    }),
    [open, selectedFiles.length]
  );

  const formatMarkdownToHtml = (text) => {
    if (!text) return '';
    return String(text)
      .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
      .replace(/\n/g, '<br>');
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text && selectedFiles.length === 0) return;

    const now = Date.now();
    if (loading || now - lastSendAtRef.current < SEND_MIN_INTERVAL_MS) {
      return;
    }
    lastSendAtRef.current = now;

    const userText = text || 'Hãy phân tích ảnh đính kèm';
    setMessages((prev) => [...prev, { id: `user-${now}`, sender: 'user', text: userText, attachmentUrls: [] }]);
    setInput('');

    const loadingId = `loading-${now}`;
    setMessages((prev) => [...prev, { id: loadingId, sender: 'ai', text: '' }]);

    setLoading(true);

    const formData = new FormData();
    formData.append('message', userText);
    selectedFiles.forEach((file) => formData.append('files', file));
    setSelectedFiles([]);

    try {
      const token = tokenStore.getToken();
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}/api/chat-ai`, {
        method: 'POST',
        headers,
        body: formData,
      });

      const payload = await response.json().catch(() => ({}));

      if (response.ok && tokenStore.getToken()) {
        await loadHistoryFromServer();
      } else {
        const reply = payload?.reply ?? '';
        setMessages((prev) =>
          prev.map((m) =>
            m.id === loadingId
              ? { ...m, text: reply || payload?.message || 'Không có phản hồi từ server.' }
              : m
          )
        );
      }
    } catch (e) {
      setMessages((prev) =>
        prev.map((m) => (m.id === loadingId ? { ...m, text: 'Lỗi kết nối đến máy chủ AI.' } : m))
      );
    } finally {
      setLoading(false);
    }
  };

  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  const handleMic = () => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'vi-VN';

    recognition.onresult = (e) => {
      const transcript = e?.results?.[0]?.[0]?.transcript;
      if (!transcript) return;
      setInput((prev) => (prev ? `${prev} ${transcript}` : transcript));
    };

    recognition.start();
  };

  if (!showAiChat && !accessLoading) {
    return null;
  }

  return (
    <>
      <div className="chat-bubble" style={styles.bubble} onClick={() => setOpen(true)}>
        <i className="fa-solid fa-robot" />
      </div>

      <div className="chat-popup" style={styles.popup}>
        <div className="chat-header-popup" style={styles.header}>
          <span>Trợ lý AI TamCare</span>
          <span className="close-btn" style={styles.closeBtn} onClick={() => setOpen(false)}>
            &times;
          </span>
        </div>

        <div style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #eee' }}>
          <AiDisclaimerBanner />
        </div>

        <div className="chat-messages-popup" style={styles.messages}>
          {historyLoading && messages.length <= 1 && (
            <div style={{ textAlign: 'center', color: '#666', fontSize: 13, padding: 8 }}>
              Đang tải lịch sử chat...
            </div>
          )}

          {messages.map((m) => {
            if (m.sender === 'ai' && loading && m.text === '') {
              return (
                <div key={m.id} className="message-popup loading" style={{ ...styles.msg, ...styles.msgLoading }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: 50,
                          background: '#28a745',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                          animationDelay: '-0.32s',
                        }}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: 50,
                          background: '#28a745',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                          animationDelay: '-0.16s',
                        }}
                      />
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: 50,
                          background: '#28a745',
                          animation: 'bounce 1.4s infinite ease-in-out both',
                          animationDelay: '0s',
                        }}
                      />
                    </div>
                    <span style={{ fontSize: 13, color: '#666', fontStyle: 'italic' }}>Đang suy nghĩ...</span>
                  </div>
                  <style>{`
                    @keyframes bounce {
                      0%, 80%, 100% { transform: scale(0); }
                      40% { transform: scale(1); }
                    }
                  `}</style>
                </div>
              );
            }

            const html = formatMarkdownToHtml(m.text);
            const attachments = Array.isArray(m.attachmentUrls) ? m.attachmentUrls : [];

            return (
              <div
                key={m.id}
                className={`message-popup ${m.sender}`}
                style={{ ...styles.msg, ...(m.sender === 'user' ? styles.msgUser : styles.msgAi) }}
              >
                {m.text ? (
                  <div
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                ) : null}
                {attachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: m.text ? 8 : 0 }}>
                    {attachments.map((url, idx) => (
                      <a
                        key={`${m.id}-img-${idx}`}
                        href={resolveMediaUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <img
                          src={resolveMediaUrl(url)}
                          alt="Đính kèm"
                          style={{ maxWidth: 120, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }}
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        <div id="previewContainerPopup" style={styles.preview}>
          {selectedFiles.map((file, idx) => (
            <div key={`${file.name}-${idx}`} className="preview-item-popup" style={styles.previewItem}>
              <img className="preview-item-popup__img" style={styles.previewImg} src={URL.createObjectURL(file)} alt={file.name} />
              <div
                className="remove-btn-popup"
                style={styles.removeBtn}
                onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
              >
                &times;
              </div>
            </div>
          ))}
        </div>

        {isLoggedIn ? (
          <div className="chat-input-wrapper-popup" style={styles.inputWrap}>
            <label htmlFor="fileInputPopup" title="Gửi ảnh" style={{ cursor: 'pointer', fontSize: 18 }}>
              📎
            </label>
            <input
              id="fileInputPopup"
              type="file"
              style={{ display: 'none' }}
              accept="image/*"
              multiple
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (!files.length) return;
                setSelectedFiles((prev) => [...prev, ...files]);
                e.target.value = '';
              }}
            />

            <button
              type="button"
              id="micBtnPopup"
              title="Nhấn để nói"
              onClick={handleMic}
              style={{ border: 'none', background: 'none', fontSize: 18, cursor: speechSupported ? 'pointer' : 'not-allowed', color: '#555' }}
              disabled={!speechSupported}
            >
              🎤
            </button>

            <input
              type="text"
              id="chatInputPopup"
              placeholder="Nhập hoặc dán ảnh..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSend();
              }}
              style={{ flex: 1, padding: '10px 14px', borderRadius: 20, border: '1px solid #ccc', outline: 'none', fontSize: 15 }}
            />

            <button type="button" id="sendBtnPopup" title="Gửi" style={styles.sendBtn} onClick={handleSend} disabled={loading || historyLoading}>
              ➤
            </button>
          </div>
        ) : (
          <div className="chat-input-wrapper-popup" style={{ ...styles.inputWrap, justifyContent: 'space-between' }}>
            <div style={{ color: '#475569', fontSize: 14 }}>Bạn cần đăng nhập để sử dụng chat</div>
            <button
              type="button"
              className="tamcare-btn-primary"
              style={{ padding: '8px 14px', borderRadius: 20, border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer' }}
              onClick={() => navigate('/login')}
            >
              Đăng nhập
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default ChatBox;
